/**
 * KB-3994 — a user-initiated sign-out must not be undone by the apex SSO door
 * (KB-3228, `apexSsoBootstrap.ts`).
 *
 * That door runs on the first signed-out auth answer of a page load and adopts a
 * live kidsbooked.com `__session`. Every pro sign-in mints one
 * (`apexSessionMint.ts`), so a sign-out whose next signed-out answer reaches the
 * door is adopted straight back into pro. There are two ways to get there:
 *
 *  - **A page that is already open.** `AuthContext`'s once-guard is spent only
 *    if the page LOADED signed out. On a page that loaded signed in, a sign-out's
 *    `onAuthStateChanged(null)` is the first signed-out answer — this tab's own,
 *    or another open tab's, arriving through shared persistence. Its custom-token
 *    GET leaves before the federated revoke's DELETE, carrying the still-valid
 *    cookie.
 *  - **The reload.** `/logout` and `/welcome` hard-navigate to `/login`. A fresh
 *    load has a fresh guard, and if the revoke did not land (it is bounded and
 *    non-fatal, see `apexSessionRevoke.ts`) the cookie is still there to adopt.
 *
 * This marker closes the reload half. The open-page half is `AuthContext`'s
 * `ssoDoorOpenRef`: any signed-in answer closes it, so that page's first
 * signed-out answer spends its one attempt without adopting.
 *
 * ## Why sessionStorage
 *
 * It survives a same-tab reload and `location.replace`, and it is scoped to the
 * one tab where the user signed out. localStorage would also suppress the door for
 * the next load of every other tab, and after a browser restart, until the next
 * pro sign-in. That
 * would close KB-3228's door for someone who signed out yesterday and followed a
 * link from kidsbooked.com today (KB-3994 AC-5).
 *
 * The cost of that scope: in the tab where someone signed out of pro, a later
 * kidsbooked.com sign-in is not adopted until they sign in to pro once there. That
 * reads as "sign in", not as a surprise session, and it is the safe direction.
 *
 * `/logout` wipes sessionStorage as part of its rescue, so it must write the
 * marker AFTER that clear. The marker is cleared by `AuthContext` on any settled
 * signed-in answer, so one sign-in re-opens the door.
 *
 * Dependency-free on purpose, like `apexSessionEndpoint.ts`: `AuthContext` and
 * `LogoutPage` import it at module scope, and many suites `vi.mock` the apex
 * behaviour modules. A marker living in one of those would read as `undefined`
 * through a partial mock.
 */

export const APEX_SSO_SUPPRESSED_KEY = "kb_pro_apex_sso_suppressed";

export function suppressApexSsoBootstrap(): void {
  try {
    sessionStorage.setItem(APEX_SSO_SUPPRESSED_KEY, "1");
  } catch (err) {
    // The federated revoke is the other half. A failed write only matters when
    // that also failed, and then this warning is the record.
    console.warn("Could not record the sign-out for the apex SSO door:", err);
  }
}

export function isApexSsoBootstrapSuppressed(): boolean {
  try {
    return sessionStorage.getItem(APEX_SSO_SUPPRESSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearApexSsoSuppression(): void {
  try {
    sessionStorage.removeItem(APEX_SSO_SUPPRESSED_KEY);
  } catch {
    // Nothing to undo: an unreadable store never suppressed the door either.
  }
}
